import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid, IconButton, TextField, Typography } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { ProductIngredient } from '../types';

interface IngredientsTabProps {
  ingredients: ProductIngredient[];
  onChange: (ingredients: ProductIngredient[]) => void;
  disabled?: boolean;
}

const IngredientsTab: FC<IngredientsTabProps> = ({ ingredients, onChange, disabled }) => {
  const { t } = useTranslation('productManagement');

  const handleAdd = () => {
    const nextId = ingredients.reduce((max, ingredient) => Math.max(max, ingredient.id), 0) + 1;
    onChange([...ingredients, { id: nextId, name: '', percentage: undefined, sortOrder: ingredients.length }]);
  };

  const handleRemove = (id: number) => {
    onChange(
      ingredients
        .filter((ingredient) => ingredient.id !== id)
        .map((ingredient, index) => ({ ...ingredient, sortOrder: index })),
    );
  };

  const handleNameChange = (id: number, value: string) => {
    onChange(ingredients.map((ingredient) => (ingredient.id === id ? { ...ingredient, name: value } : ingredient)));
  };

  const handlePercentageChange = (id: number, value: string) => {
    const percentage = value === '' ? undefined : Number(value);
    onChange(ingredients.map((ingredient) => (ingredient.id === id ? { ...ingredient, percentage } : ingredient)));
  };

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="textLG"
        sx={{ mb: 4, fontWeight: 600 }}>
        {t('ingredients', 'Ingredients')}
      </Typography>
      <Grid container
        spacing={3}>
        {ingredients.length === 0 && (
          <Grid item
            xs={12}>
            <Typography variant="textSM"
              color="text.secondary">
              {t('noIngredientsCaptured', 'No ingredients captured yet.')}
            </Typography>
          </Grid>
        )}
        {ingredients.map((ingredient, index) => (
          <Grid container
            item
            xs={12}
            spacing={2}
            alignItems="center"
            key={ingredient.id}>
            <Grid item
              xs={1}>
              <Typography variant="textSM"
                color="text.secondary">{index + 1}</Typography>
            </Grid>
            <Grid item
              xs={7}
              md={8}>
              <TextField
                label={t('ingredientName', 'Ingredient')}
                value={ingredient.name}
                onChange={(e) => handleNameChange(ingredient.id, e.target.value)}
                disabled={disabled}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item
              xs={3}
              md={2}>
              <TextField
                label={t('ingredientPercentage', 'Percentage (%)')}
                type="number"
                value={ingredient.percentage ?? ''}
                onChange={(e) => handlePercentageChange(ingredient.id, e.target.value)}
                disabled={disabled}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item
              xs={1}>
              <IconButton
                onClick={() => handleRemove(ingredient.id)}
                disabled={disabled}
                size="small"
                aria-label={t('delete', 'Delete')}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        ))}
        <Grid item
          xs={12}>
          <Button startIcon={<Add />}
            onClick={handleAdd}
            disabled={disabled}>
            {t('addIngredient', 'Add Ingredient')}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default IngredientsTab;
